import { useEffect, useState } from "react";
import { usePhotos } from "@/hooks/usePhotos";
import { photoUrl } from "@/lib/api";
import { EmptyFrame } from "@/components/Photo";
import { SectionHeading } from "@/components/Reveal";

const placeholders = (n, label) => Array.from({ length: n }, (_, i) => ({ id: `ph-${i}`, placeholder: true, label: `${label} ${i + 1}` }));

export const Coverflow = ({ photos, minSlots = 5, placeholderLabel = "Photo", interval = 3200, height = "h-[300px] sm:h-[380px]", slide = "w-[62%] max-w-[420px] sm:w-[46%]", spread = [46, 62], testid = "coverflow", alt = "" }) => {
  const items = photos.length >= 2 ? photos : [...photos, ...placeholders(Math.max(0, minSlots - photos.length), placeholderLabel)];
  const [idx, setIdx] = useState(0);
  const n = items.length;

  useEffect(() => {
    if (n < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), interval);
    return () => clearInterval(t);
  }, [n, interval]);

  const step = typeof window !== "undefined" && window.innerWidth < 640 ? spread[0] : spread[1];
  return (
    <div data-testid={testid} data-count={n} className={`relative mx-auto w-full select-none ${height}`} style={{ perspective: "1400px" }} aria-live="off">
      {items.map((p, i) => {
        let off = i - idx;
        if (off > n / 2) off -= n;
        if (off < -n / 2) off += n;
        const abs = Math.abs(off);
        const visible = abs <= 2;
        return (
          <div
            key={p.id}
            data-testid={`${testid}-slide-${i}`}
            data-center={off === 0 ? "true" : "false"}
            className={`pointer-events-none absolute left-1/2 top-0 flex h-full items-center justify-center ${slide}`}
            style={{
              transform: `translateX(calc(-50% + ${off * step}%)) translateZ(${-abs * 160}px) rotateY(${-off * 28}deg) scale(${1 - abs * 0.08})`,
              opacity: visible ? 1 - abs * 0.28 : 0,
              zIndex: 10 - abs,
              transition: "transform 0.9s cubic-bezier(0.22,1,0.36,1), opacity 0.9s ease",
              transformStyle: "preserve-3d",
            }}
          >
            {p.placeholder ? (
              <EmptyFrame ratio="h-full w-full" label={p.label} rounded="rounded-3xl" className="shadow-layered" />
            ) : (
              <img decoding="async" src={photoUrl(p)} alt={alt} draggable={false} loading={i < 3 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : "auto"} className="max-h-full w-auto max-w-full rounded-3xl border border-white/70 object-contain shadow-layered" />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ReviewsShowcase = () => {
  const { photos } = usePhotos("reviews");
  return (
    <section id="reviews" data-testid="reviews-section" className="overflow-hidden bg-ivory py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading big eyebrow="Real Reviews" title="Why Students Choose Us" />
      </div>
      <div className="mt-14">
        <Coverflow photos={photos} placeholderLabel="Review screenshot" testid="reviews-showcase" />
      </div>
    </section>
  );
};
